import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Pencil, AlertTriangle, Package } from "lucide-react";
import api, { friendlyError } from "@/lib/api";
import { photoUrl } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import Layout from "@/components/Layout";
import { Card, Chip, Button, Skeleton } from "@/ui";

const MODE_LABEL = { product: "one count for the item", size: "counted per size", option: "counted per option" };

export default function ProductDetail() {
  const { productId } = useParams();
  const { shopId, shopName } = useAuth();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [names, setNames] = useState({});
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!shopId) { navigate("/pick-shop", { replace: true }); return; }
    setLoading(true);
    api.get(`/products/${productId}?shopId=${shopId}`).then((r) => setProduct(r.data)).catch((e) => setError(friendlyError(e))).finally(() => setLoading(false));
    api.get(`/products?shopId=${shopId}`).then((r) => {
      const m = {}; r.data.products.forEach((p) => { m[p.id] = p.name; }); setNames(m);
    }).catch(() => {});
  }, [shopId, productId, navigate]);

  const sizes = (product?.variants || []).filter((v) => v.kind === "size");
  const flavours = (product?.variants || []).filter((v) => v.kind === "flavour");

  return (
    <Layout shopName={shopName}>
      <div className="row-between" style={{ marginBottom: 16 }}>
        <button className="btn btn-ghost btn-sm" data-testid="back-to-products" onClick={() => navigate("/products")}>
          <ArrowLeft size={20} strokeWidth={1.75} />back to items
        </button>
        {product?.canEdit ? (
          <Button variant="primary" data-testid="edit-product-btn" onClick={() => navigate(`/products/${productId}/edit`)}>
            <Pencil size={16} strokeWidth={1.75} />edit item
          </Button>
        ) : null}
      </div>
      {error ? <div className="err-text" data-testid="product-error" style={{ marginBottom: 12 }}>{error}</div> : null}

      {loading ? (
        <div className="grid grid-2"><Skeleton variant="card" /><div className="stack"><Skeleton variant="title" /><Skeleton /><Skeleton /></div></div>
      ) : product ? (
        <div className="grid grid-2" style={{ alignItems: "start" }}>
          {product.photos?.[0] ? (
            <img className="thumb" src={photoUrl(product.photos[0])} alt={product.name} loading="lazy" style={{ aspectRatio: "4/3", height: "auto" }} />
          ) : (
            <div className="thumb" style={{ aspectRatio: "4/3", height: "auto", display: "grid", placeItems: "center", background: "var(--surface-2)" }}>
              <Package size={40} strokeWidth={1.1} style={{ opacity: 0.4 }} />
            </div>
          )}
          <div>
            <div className="row-between">
              <h1 className="t-h1">{product.name}</h1>
              {product.soldOut ? <Chip tone="bad" dot data-testid="product-soldout">sold out</Chip>
                : product.lowStock ? <Chip tone="warn" dot data-testid="product-lowstock"><AlertTriangle size={13} strokeWidth={2} />{product.available} left</Chip>
                : <Chip tone="good" dot>{product.available} left</Chip>}
            </div>
            <div className="t-display num" style={{ margin: "8px 0" }}>{product.price}</div>
            <p className="muted-3 t-small" style={{ marginBottom: 12 }}>{product.category}{!product.active ? " · hidden from customers" : ""}</p>
            {product.description ? <p className="t-body section">{product.description}</p> : null}

            {sizes.length ? (
              <Card className="section">
                <p className="t-label muted" style={{ marginBottom: 12 }}>sizes</p>
                <div className="stack">
                  {sizes.map((v, i) => (
                    <div key={i} className="row-between"><span className="t-body">{v.name}{v.soldOut ? <span className="muted-3 t-small"> · sold out</span> : null}</span><span className="num">{v.price}</span></div>
                  ))}
                </div>
              </Card>
            ) : null}
            {flavours.length ? (
              <Card className="section">
                <p className="t-label muted" style={{ marginBottom: 12 }}>flavours</p>
                <div className="stack">
                  {flavours.map((v, i) => (
                    <div key={i} className="row-between"><span className="t-body">{v.name}</span><span className="num">{v.priceCentavos ? v.price : "—"}</span></div>
                  ))}
                </div>
              </Card>
            ) : null}

            <Card className="section">
              <div className="row-between" style={{ marginBottom: 12 }}>
                <p className="t-label muted">stock</p>
                <span className="muted-3 t-small">{MODE_LABEL[product.stockMode] || ""}</span>
              </div>
              <div className="stack" data-testid="stock-lines">
                {product.stockLines.map((s, i) => (
                  <div key={i} className="row-between">
                    <span className="t-body">{s.variantName ? s.variantName : "how many left"}</span>
                    <span className="num">{s.available} left {s.reserved ? <span className="muted-3 t-small">({s.reserved} being bought now)</span> : null}</span>
                  </div>
                ))}
              </div>
            </Card>

            {product.goesWellWith?.length ? (
              <Card className="section">
                <p className="t-label muted" style={{ marginBottom: 12 }}>goes well with</p>
                <div className="row" style={{ gap: 8, flexWrap: "wrap" }}>
                  {product.goesWellWith.map((id) => (
                    <button key={id} className="chip" style={{ cursor: "pointer" }} onClick={() => navigate(`/products/${id}`)}>{names[id] || "item"}</button>
                  ))}
                </div>
              </Card>
            ) : null}

            {!product.canEdit ? <p className="muted-3 t-small" style={{ marginTop: 12 }}>only the shop owner can change items and prices.</p> : null}
          </div>
        </div>
      ) : null}
    </Layout>
  );
}
